import { create } from 'zustand';
import { Document, SearchFilters, ProcessingStatus, DocumentStatus, ExtractionChoice } from '../types';
import { apiService, type DuplicateUploadContext } from '../services/apiService';
import { deduplicateRequest, generateRequestKey } from '../utils/requestDeduplication';

const SEARCH_EMPTY_PAGE_FOLLOW_CAP = 4;

async function fetchDocumentsPage(
  searchFilters: SearchFilters,
  itemsPerPage: number,
  page: number,
  prevToken: string | null | undefined
): Promise<{ documents: Document[]; nextToken?: string | null }> {
  const requestKey = generateRequestKey('loadDocumentsPage', {
    ...searchFilters,
    page,
    itemsPerPage,
    nextToken: prevToken ?? null,
  });

  let { documents, nextToken } = await deduplicateRequest(requestKey, () =>
    apiService.getDocuments(searchFilters, {
      limit: itemsPerPage,
      nextToken: prevToken ?? undefined,
    })
  );

  const query = (searchFilters.query || '').trim();
  const followEmptySlice =
    !!query ||
    !!(searchFilters.dateFrom || searchFilters.dateTo) ||
    !!(searchFilters.fieldFilters && Object.keys(searchFilters.fieldFilters).length > 0);
  let follows = 0;
  while (
    followEmptySlice &&
    documents.length === 0 &&
    nextToken &&
    follows < SEARCH_EMPTY_PAGE_FOLLOW_CAP
  ) {
    follows += 1;
    const followKey = generateRequestKey('loadDocumentsPageFollow', {
      ...searchFilters,
      page,
      itemsPerPage,
      follow: follows,
      nextToken,
    });
    const followToken = nextToken;
    const again = await deduplicateRequest(followKey, () =>
      apiService.getDocuments(searchFilters, {
        limit: itemsPerPage,
        nextToken: followToken,
      })
    );
    documents = again.documents;
    nextToken = again.nextToken;
  }

  return { documents, nextToken };
}

interface DocumentStore {
  documents: Document[];
  loading: boolean;
  error: string | null;
  searchFilters: SearchFilters;
  selectedDocument: Document | null;
  processingStatuses: Map<string, ProcessingStatus>;

  // Actions
  loadDocuments: (filters?: SearchFilters) => Promise<void>;
  loadDocumentsPage: (page: number, itemsPerPage: number, filters?: SearchFilters) => Promise<void>;
  /** Clears pagination cache and refetches from the API through the requested page (correct token chain). */
  refreshDocumentsPage: (page: number, itemsPerPage: number, filters?: SearchFilters) => Promise<void>;
  setSearchFilters: (filters: SearchFilters) => void;
  selectDocument: (id: string | null) => Promise<void>;
  uploadDocument: (
    file: File,
    extractionChoice: ExtractionChoice,
    duplicate?: DuplicateUploadContext
  ) => Promise<{ documentId: string }>;
  approveDocument: (id: string, storedFields: Record<string, any>, customFields: any[]) => Promise<void>;
  rejectDocument: (id: string) => Promise<void>;
  updateDocument: (id: string, updates: Partial<Document>) => Promise<void>;
  deleteDocument: (id: string, permanent?: boolean) => Promise<void>;
  bulkDeleteDocuments: (ids: string[], permanent?: boolean) => Promise<{ successCount: number; failedIds: string[] }>;
  restoreDocument: (id: string) => Promise<void>;
  getProcessingStatus: (id: string) => Promise<ProcessingStatus | null>;
  logAction: (id: string, action: string) => Promise<void>;
  clearError: () => void;

  // Pagination cache
  documentsByPage: Record<number, Document[]>;
  nextTokenByPage: Record<number, string | null>;
  currentPage: number;
  itemsPerPage: number;
  resetPagination: () => void;
}

export const useDocumentStore = create<DocumentStore>((set, get) => ({
  documents: [],
  loading: false,
  error: null,
  searchFilters: {},
  selectedDocument: null,
  processingStatuses: new Map(),
  documentsByPage: {},
  nextTokenByPage: { 1: null },
  currentPage: 1,
  itemsPerPage: 12,

  loadDocuments: async (filters) => {
    const { currentPage, itemsPerPage } = get();
    await get().loadDocumentsPage(currentPage, itemsPerPage, filters);
  },

  loadDocumentsPage: async (page, itemsPerPage, filters) => {
    set({ loading: true, error: null });
    const searchFilters = filters || get().searchFilters;
    const state = get();

    const cached = state.documentsByPage[page];
    if (cached && cached.length > 0) {
      set({
        documents: cached,
        currentPage: page,
        itemsPerPage,
        loading: false,
      });
      return;
    }

    try {
      const prevToken = state.nextTokenByPage[page] ?? (page === 1 ? null : undefined);
      const { documents, nextToken } = await fetchDocumentsPage(
        searchFilters,
        itemsPerPage,
        page,
        prevToken
      );

      const documentsByPage = { ...state.documentsByPage, [page]: documents };
      const nextTokenByPage = { ...state.nextTokenByPage };
      nextTokenByPage[page + 1] = nextToken ?? null;

      set({
        documents,
        documentsByPage,
        nextTokenByPage,
        currentPage: page,
        itemsPerPage,
        loading: false,
      });
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  refreshDocumentsPage: async (page, itemsPerPage, filters) => {
    const searchFilters = filters ?? get().searchFilters;
    set({
      loading: true,
      error: null,
      documentsByPage: {},
      nextTokenByPage: { 1: null },
    });

    try {
      let documents: Document[] = [];
      const documentsByPage: Record<number, Document[]> = {};
      let nextTokenByPage: Record<number, string | null> = { 1: null };

      for (let p = 1; p <= page; p++) {
        const prevToken = nextTokenByPage[p] ?? (p === 1 ? null : undefined);
        const { documents: pageDocs, nextToken } = await fetchDocumentsPage(
          searchFilters,
          itemsPerPage,
          p,
          prevToken
        );
        documents = pageDocs;
        documentsByPage[p] = pageDocs;
        nextTokenByPage = { ...nextTokenByPage, [p + 1]: nextToken ?? null };
      }

      set({
        documents,
        documentsByPage,
        nextTokenByPage,
        currentPage: page,
        itemsPerPage,
        loading: false,
      });
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  setSearchFilters: (filters) => {
    set({ searchFilters: filters });
    get().resetPagination();
    get().loadDocumentsPage(1, get().itemsPerPage, filters);
  },

  selectDocument: async (id) => {
    if (!id) {
      set({ selectedDocument: null });
      return;
    }
    
    const requestKey = generateRequestKey('selectDocument', id);
    set({ loading: true });
    
    try {
      const doc = await deduplicateRequest(requestKey, () =>
        apiService.getDocument(id)
      );
      if (doc) {
        await get().logAction(id, 'viewed');
      }
      set({ selectedDocument: doc, loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  uploadDocument: async (file, extractionChoice, duplicate) => {
    // Don't set loading state here - let the Upload component handle it
    // This prevents interference between multiple sequential uploads
    try {
      console.log(`[Store] Starting upload for: ${file.name}`);
      const { documentId, status, document: newDoc } = await apiService.uploadDocument(
        file,
        extractionChoice,
        duplicate
      );
      console.log(`[Store] Upload API call completed for: ${file.name}, documentId: ${documentId}, status: ${status.stage}`);
      get().processingStatuses.set(documentId, status);
      set({ processingStatuses: new Map(get().processingStatuses) });
      
      // Upload completes synchronously, so immediately reload documents
      // Also log the 'created' action for audit trail
      if (status.stage === 'completed') {
        await get().logAction(documentId, 'created');
        
        // Add the document optimistically using the data we already have from upload
        // This ensures it appears immediately even if the list query has eventual consistency issues
        if (newDoc) {
          const currentDocs = get().documents;
          // Check if document already exists (avoid duplicates)
          if (!currentDocs.find(d => d.id === documentId)) {
            // Add the document to the beginning of the list
            set({ documents: [newDoc, ...currentDocs] });
            console.log(`[Store] Added document ${documentId} optimistically to store`);
          }
        } else {
          // Fallback: try to fetch the document if we don't have it
          try {
            const fetchedDoc = await apiService.getDocument(documentId);
            if (fetchedDoc) {
              const currentDocs = get().documents;
              if (!currentDocs.find(d => d.id === documentId)) {
                set({ documents: [fetchedDoc, ...currentDocs] });
                console.log(`[Store] Fetched and added document ${documentId} to store`);
              }
            }
          } catch (error) {
            console.warn('Failed to fetch newly uploaded document:', error);
            // Continue with reload anyway
          }
        }
        
        // Reload documents with a query that includes 'pending' status
        // This uses the GSI which is more reliable than a full scan
        // Add a small delay to account for DynamoDB eventual consistency (GSI propagation can take 1-2 seconds)
        setTimeout(async () => {
          const currentFilters = get().searchFilters;
          // Query for documents including 'pending' status to ensure the new document appears
          // If current filters already have a status, include 'pending' in the array
          // Otherwise, query for ['pending', 'active'] to show relevant documents
          let statusFilter: DocumentStatus[] | undefined;
          if (currentFilters.status) {
            // If there's already a status filter, ensure 'pending' is included
            const currentStatusArray = Array.isArray(currentFilters.status) 
              ? currentFilters.status 
              : [currentFilters.status];
            statusFilter = currentStatusArray.includes('pending') 
              ? currentStatusArray 
              : [...currentStatusArray, 'pending'];
          } else {
            // No status filter - query for active and pending (most common case)
            statusFilter = ['pending', 'active'];
          }
          
          await get().loadDocuments({ ...currentFilters, status: statusFilter });
        }, 3000); // Increased delay to account for DynamoDB eventual consistency (GSI can take 2-5 seconds)
      } else if (status.stage === 'failed') {
        // Failed upload - no need to reload
      } else {
        // For pending/processing states, set a timeout to reload after a delay
        // This ensures the document appears even if status tracking isn't perfect
        setTimeout(async () => {
          const currentFilters = get().searchFilters;
          // Query for documents including 'pending' status
          let statusFilter: DocumentStatus[] | undefined;
          if (currentFilters.status) {
            const currentStatusArray = Array.isArray(currentFilters.status) 
              ? currentFilters.status 
              : [currentFilters.status];
            statusFilter = currentStatusArray.includes('pending') 
              ? currentStatusArray 
              : [...currentStatusArray, 'pending'];
          } else {
            statusFilter = ['pending', 'active'];
          }
          
          await get().loadDocuments({ ...currentFilters, status: statusFilter });
        }, 4000); // Longer delay for processing states to account for GSI eventual consistency
      }

      return { documentId };
    } catch (error: any) {
      // Re-throw error so Upload component can handle it
      throw error;
    }
  },

  approveDocument: async (id, storedFields, customFields) => {
    set({ loading: true, error: null });
    try {
      await apiService.approveDocument(id, storedFields, customFields);
      await get().loadDocuments();
      set({ loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  rejectDocument: async (id) => {
    set({ loading: true, error: null });
    try {
      await apiService.rejectDocument(id);
      await get().loadDocuments();
      set({ loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  updateDocument: async (id, updates) => {
    set({ loading: true, error: null });
    try {
      // Update document via API
      const updatedDoc = await apiService.updateDocument(id, updates);
      
      // Optimistically update the document in the documents array
      const currentDocs = get().documents;
      const updatedDocs = currentDocs.map(doc => 
        doc.id === id ? { ...doc, ...updates, ...updatedDoc } : doc
      );
      set({ documents: updatedDocs });
      
      // Refresh documents list to ensure consistency (with current filters)
      await get().loadDocuments(get().searchFilters);
      
      // Update selected document if it's the one being edited
      if (get().selectedDocument?.id === id) {
        await get().selectDocument(id);
      }
      set({ loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      // On error, reload documents to get correct state
      await get().loadDocuments(get().searchFilters);
    }
  },

  deleteDocument: async (id, permanent = false) => {
    set({ loading: true, error: null });
    try {
      await apiService.deleteDocument(id, permanent);
      await get().loadDocuments();
      set({ loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  bulkDeleteDocuments: async (ids, permanent = false) => {
    const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
    if (uniqueIds.length === 0) {
      return { successCount: 0, failedIds: [] };
    }

    set({ loading: true, error: null });
    const failedIds: string[] = [];
    let successCount = 0;

    try {
      for (const id of uniqueIds) {
        try {
          await apiService.deleteDocument(id, permanent);
          successCount += 1;
        } catch {
          failedIds.push(id);
        }
      }

      // Reload once after all delete operations complete.
      await get().loadDocuments();
      set({
        loading: false,
        ...(failedIds.length > 0
          ? { error: `Failed to delete ${failedIds.length} document(s).` }
          : {}),
      });

      return { successCount, failedIds };
    } catch (error: any) {
      set({ error: error.message, loading: false });
      return { successCount, failedIds: [...failedIds, ...uniqueIds.slice(successCount)] };
    }
  },

  restoreDocument: async (id) => {
    set({ loading: true, error: null });
    try {
      await apiService.restoreDocument(id);
      // Reload documents without status filter to ensure restored document (status: 'active') is included
      // This is especially important for the search page which should show all documents
      const currentFilters = get().searchFilters;
      const filtersWithoutStatus = { ...currentFilters, status: undefined };
      await get().loadDocuments(filtersWithoutStatus);
      set({ loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  getProcessingStatus: async (id) => {
    const status = await apiService.getProcessingStatus(id);
    if (status) {
      get().processingStatuses.set(id, status);
      set({ processingStatuses: new Map(get().processingStatuses) });
    }
    return status;
  },

  logAction: async (id, action) => {
    await apiService.logAction(id, action);
  },

  clearError: () => set({ error: null }),

  resetPagination: () => {
    set({
      documentsByPage: {},
      nextTokenByPage: { 1: null },
      currentPage: 1,
    });
  },
}));

